import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { tokenStorage } from '../api/dealerApiClient'
import { getMe, login as loginService, logout as logoutService } from '../services/authService'
import type { DealerMe, DealerLoginRequest } from '../types'

interface DealerAuthContextValue {
  me: DealerMe | null
  isLoading: boolean
  login: (credentials: DealerLoginRequest, remember?: boolean) => Promise<void>
  logout: () => Promise<void>
}

const DealerAuthContext = createContext<DealerAuthContextValue | null>(null)

export function DealerAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<DealerMe | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = tokenStorage.getAccess()
    if (!token) {
      setIsLoading(false)
      return
    }
    getMe()
      .then(setMe)
      .catch(() => {
        tokenStorage.clear()
      })
      .finally(() => setIsLoading(false))
  }, [])

  async function login(credentials: DealerLoginRequest, remember = true) {
    await loginService(credentials, remember)
    const user = await getMe()
    setMe(user)
  }

  async function logout() {
    await logoutService()
    setMe(null)
  }

  return (
    <DealerAuthContext.Provider value={{ me, isLoading, login, logout }}>
      {children}
    </DealerAuthContext.Provider>
  )
}

export function useDealerAuth() {
  const ctx = useContext(DealerAuthContext)
  if (!ctx) throw new Error('useDealerAuth must be used within DealerAuthProvider')
  return ctx
}
